/**
 * Property AST writers for V2 editor
 *
 * These functions MUTATE / rewrite source code — setting and removing component
 * props and style props, ensuring imports and hook bindings. Parsing helpers and
 * read-only inspection live in `@/ast/readers/properties/parse`.
 *
 * They are designed to be pure functions for easy testing.
 */

import { parseExpression as babelParseExpression } from '@babel/parser';
import {
  arrayExpression,
  callExpression,
  identifier,
  importDeclaration,
  importSpecifier,
  jsxAttribute,
  jsxClosingElement,
  jsxExpressionContainer,
  jsxIdentifier,
  jsxText,
  numericLiteral,
  objectExpression,
  objectProperty,
  stringLiteral,
  variableDeclaration,
  variableDeclarator,
} from '@babel/types';
import { parse, types, print } from 'recast';

import {
  createParser,
  findJSXElementInAST,
  getFileKey,
  unwrapStyleExpression,
} from '@/ast/readers/properties/parse';
import type { FileContents, NodeLocation, TransformResult } from '@/ast/readers/properties/types';

export type { FileContents, NodeLocation, TransformResult } from '@/ast/readers/properties/types';

/**
 * Set a component property (non-style) on a JSX element
 *
 * @param files - The current file contents
 * @param node - The node location (file, line, col)
 * @param key - The property name to set
 * @param value - The value to set
 * @param valueType - The type of value ('string', 'number', 'boolean')
 * @returns TransformResult with updated files or error
 */
export function setProperty(
  files: FileContents,
  node: NodeLocation,
  key: string,
  value: any,
  valueType: 'string' | 'number' | 'boolean' | 'expression',
): TransformResult {
  const fileKey = getFileKey(node);
  const file = files[fileKey];

  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return {
      success: false,
      updatedFiles: files,
      error: `File not found or not a code file: ${fileKey}`,
    };
  }

  const fileContents = file.contents;

  try {
    const parser = createParser(fileKey);
    const ast = parse(fileContents, { parser });
    let foundNode = false;
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);

    const matchedPath = findJSXElementInAST(ast, targetLine, targetCol, node.type);
    if (matchedPath) {
      foundNode = true;
      const jsxElement = matchedPath.node as any;
      const openingElement = jsxElement.openingElement;

      if (key === 'children') {
        const strVal = String(value);
        let newChildren: any[];

        if (/\{/.test(strVal)) {
          // Value contains expressions — parse as real JSX children so braces become
          // JSXExpressionContainer nodes instead of being written as literal characters.
          try {
            const wrapped = `<__RootElement__>${strVal}</__RootElement__>`;
            const tempAst = parse(wrapped, { parser });
            const rootExpr = (tempAst as any).program?.body?.[0]?.expression;
            if (rootExpr?.type === 'JSXElement' && Array.isArray(rootExpr.children)) {
              newChildren = rootExpr.children;
            } else {
              newChildren = [jsxText(strVal)];
            }
          } catch {
            newChildren = [jsxText(strVal)];
          }
        } else {
          newChildren = [jsxText(strVal)];
        }

        jsxElement.children = newChildren;
        if (openingElement.selfClosing) {
          openingElement.selfClosing = false;
          if (!jsxElement.closingElement) {
            const closingName = openingElement.name?.name || 'unknown';
            jsxElement.closingElement = jsxClosingElement(jsxIdentifier(closingName));
          }
        }
      } else {
        // Handle regular attributes
        const attributes = openingElement.attributes || [];
        const existingAttr = attributes.find(
          (attr: any) => attr.type === 'JSXAttribute' && attr.name?.name === key,
        );

        let valueNode: any;
        if (valueType === 'string') {
          valueNode = { type: 'StringLiteral', value };
        } else if (valueType === 'number') {
          valueNode = {
            type: 'JSXExpressionContainer',
            expression: { type: 'NumericLiteral', value },
          };
        } else if (valueType === 'boolean') {
          valueNode =
            value === true
              ? null
              : {
                  type: 'JSXExpressionContainer',
                  expression: { type: 'BooleanLiteral', value },
                };
        } else if (valueType === 'expression') {
          try {
            const exprAst = babelParseExpression(String(value), {
              plugins: ['jsx', 'typescript', 'objectRestSpread'],
            });
            valueNode = { type: 'JSXExpressionContainer', expression: exprAst };
          } catch {
            valueNode = { type: 'StringLiteral', value: String(value) };
          }
        } else {
          valueNode = {
            type: 'JSXExpressionContainer',
            expression: { type: 'StringLiteral', value: String(value) },
          };
        }

        if (existingAttr) {
          existingAttr.value = valueNode;
        } else {
          openingElement.attributes.push({
            type: 'JSXAttribute',
            name: { type: 'JSXIdentifier', name: key },
            value: valueNode,
          });
        }
      }
    }

    if (!foundNode) {
      return {
        success: false,
        updatedFiles: files,
        error: `Node not found at line ${node.line}, col ${node.col} in ${fileKey}`,
      };
    }

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: {
        ...files,
        [fileKey]: {
          type: 'CODE' as const,
          contents: updatedCode,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Remove a JSX attribute (prop) from a component entirely.
 */
export function removeProperty(
  files: FileContents,
  node: NodeLocation,
  key: string,
): TransformResult {
  const fileKey = getFileKey(node);
  const file = files[fileKey];

  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return {
      success: false,
      updatedFiles: files,
      error: `File not found: ${fileKey}`,
    };
  }

  try {
    const parser = createParser(fileKey);
    const ast = parse(file.contents, { parser });
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);
    let foundNode = false;

    const matchedPath = findJSXElementInAST(ast, targetLine, targetCol, (node as any).type);
    if (matchedPath) {
      foundNode = true;
      const jsxElement = matchedPath.node as any;
      const openingElement = jsxElement.openingElement;

      if (key === 'children') {
        // children are stored as JSX content (jsxElement.children), not as an attribute —
        // setProperty handles them via jsxElement.children and removeProperty must do the same.
        jsxElement.children = [];
        openingElement.selfClosing = true;
        jsxElement.closingElement = null;
      } else {
        openingElement.attributes = (openingElement.attributes || []).filter(
          (attr: any) => !(attr.type === 'JSXAttribute' && attr.name?.name === key),
        );
      }
    }

    if (!foundNode) {
      return {
        success: false,
        updatedFiles: files,
        error: `Node not found at ${node.line}:${node.col}`,
      };
    }

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: { ...files, [fileKey]: { ...file, contents: updatedCode } },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Ensure a named import exists in a file (e.g. ensure Linking is imported from react-native).
 * Adds the specifier to an existing import declaration, or creates a new one.
 */
export function ensureImport(
  files: FileContents,
  fileKey: string,
  source: string,
  importedName: string,
): TransformResult {
  const file = files[fileKey];
  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return {
      success: false,
      updatedFiles: files,
      error: `File not found: ${fileKey}`,
    };
  }
  try {
    const parser = createParser(fileKey);
    const ast = parse(file.contents, { parser });
    let existingImport: any = null;
    let alreadyImported = false;

    (ast.program.body as any[]).forEach((node: any) => {
      if (node.type === 'ImportDeclaration' && node.source?.value === source) {
        existingImport = node;
        const already = node.specifiers?.some(
          (s: any) =>
            s.type === 'ImportSpecifier' &&
            (s.imported?.name === importedName || s.local?.name === importedName),
        );
        if (already) alreadyImported = true;
      }
    });

    if (alreadyImported) {
      return { success: true, updatedFiles: files };
    }

    if (existingImport) {
      existingImport.specifiers = [
        ...(existingImport.specifiers || []),
        importSpecifier(identifier(importedName), identifier(importedName)),
      ];
    } else {
      const newImport = importDeclaration(
        [importSpecifier(identifier(importedName), identifier(importedName))],
        stringLiteral(source),
      );
      (ast.program.body as any[]).unshift(newImport);
    }

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: { ...files, [fileKey]: { ...file, contents: updatedCode } },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/** Does a function bind `name` — as a `const name = …` (or destructured
 *  `{ name }`) in its body, or as a param (identifier or object-pattern)? */
function fnBindsName(fn: any, name: string): boolean {
  for (const param of fn.params || []) {
    if (param?.type === 'Identifier' && param.name === name) return true;
    if (param?.type === 'ObjectPattern') {
      for (const pr of param.properties || []) {
        const key = pr.key;
        if (key && (key.name === name || key.value === name)) return true;
      }
    }
  }
  const body = fn.body;
  if (body?.type === 'BlockStatement') {
    for (const stmt of body.body || []) {
      if (stmt?.type !== 'VariableDeclaration') continue;
      for (const d of stmt.declarations || []) {
        if (d.id?.type === 'Identifier' && d.id.name === name) return true;
        if (d.id?.type === 'ObjectPattern') {
          for (const pr of d.id.properties || []) {
            const key = pr.key;
            if (key && (key.name === name || key.value === name)) return true;
          }
        }
      }
    }
  }
  return false;
}

/**
 * Ensure a hook binding like `const navigation = useNavigation();` exists for
 * the component that references `<varName>` (e.g. an `onPress` the inspector
 * just wrote as `navigation.navigate(...)`). Inserts the declaration at the top
 * of the OUTERMOST function enclosing the target JSX node — the component, which
 * every inner callback (onPress, a `.map` render) can close over — but only when
 * `<varName>` isn't already in scope (declared or received as a prop). It's a
 * no-op (still `success: true`) when the element can't be located or the
 * enclosing function has an expression body, so the caller's other edits stand.
 */
export function ensureHook(
  files: FileContents,
  node: NodeLocation,
  varName: string,
  hookName: string,
): TransformResult {
  const fileKey = getFileKey(node);
  const file = files[fileKey];
  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return { success: false, updatedFiles: files, error: `File not found: ${fileKey}` };
  }
  try {
    const ast = parse(file.contents, { parser: createParser(fileKey) });
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);
    const jsxPath = findJSXElementInAST(ast, targetLine, targetCol, (node as any).type);
    if (!jsxPath) return { success: true, updatedFiles: files };

    // Enclosing functions, innermost → outermost.
    const fns: any[] = [];
    let p: any = jsxPath;
    while (p) {
      const n = p.node;
      if (
        n &&
        (n.type === 'FunctionDeclaration' ||
          n.type === 'FunctionExpression' ||
          n.type === 'ArrowFunctionExpression')
      ) {
        fns.push(n);
      }
      p = p.parentPath;
    }
    if (fns.length === 0) return { success: true, updatedFiles: files };

    // Already in scope anywhere (own declaration or a prop) → nothing to do.
    if (fns.some((fn) => fnBindsName(fn, varName))) {
      return { success: true, updatedFiles: files };
    }

    // Insert at the top of the component (outermost enclosing function). Hooks
    // must run at the component's top level, and inner callbacks close over it.
    const component = fns[fns.length - 1];
    const body = component.body;
    if (!body || body.type !== 'BlockStatement') {
      return { success: true, updatedFiles: files };
    }
    body.body.unshift(
      variableDeclaration('const', [
        variableDeclarator(identifier(varName), callExpression(identifier(hookName), [])),
      ]),
    );

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: { ...files, [fileKey]: { ...file, contents: updatedCode } },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Helper to update or add a property in an ObjectExpression (inline style)
 */
function makeStyleValueNode(
  value: string | number,
  valueType: 'string' | 'number' | 'expression',
): any {
  if (valueType === 'expression') {
    try {
      return babelParseExpression(String(value), {
        plugins: ['jsx', 'typescript', 'objectRestSpread'],
      });
    } catch {
      return stringLiteral(String(value));
    }
  }
  return valueType === 'string' ? stringLiteral(value as string) : numericLiteral(value as number);
}

/**
 * Returns true if a value (typically `valueType === 'expression'`) parses to an
 * AST containing only static literals — no free identifiers, member expressions,
 * function calls, or template substitutions. Static expressions can safely live
 * inside `StyleSheet.create({...})` at module scope; dynamic ones cannot, since
 * they reference runtime bindings (theme, props, etc.) that aren't available there.
 */
function isStaticExpression(value: string | number): boolean {
  if (typeof value === 'number') return true;
  let node: any;
  try {
    node = babelParseExpression(String(value), {
      plugins: ['jsx', 'typescript', 'objectRestSpread'],
    });
  } catch {
    return false;
  }
  return isStaticAstNode(node);
}

function isStaticAstNode(node: any): boolean {
  if (!node) return false;
  switch (node.type) {
    case 'StringLiteral':
    case 'NumericLiteral':
    case 'BooleanLiteral':
    case 'NullLiteral':
      return true;
    case 'UnaryExpression':
      // Allow -5, +5, !true (literal sign / negation)
      return ['-', '+', '!'].includes(node.operator) && isStaticAstNode(node.argument);
    case 'TemplateLiteral':
      // Allow only template literals with no ${} substitutions
      return !node.expressions || node.expressions.length === 0;
    case 'ArrayExpression':
      return (node.elements || []).every((el: any) => el == null || isStaticAstNode(el));
    case 'ObjectExpression':
      return (node.properties || []).every((p: any) => {
        if (p.type !== 'ObjectProperty') return false; // rejects spread, methods, shorthand-with-issues
        const keyOk = p.computed
          ? isStaticAstNode(p.key)
          : p.key?.type === 'Identifier' ||
            p.key?.type === 'StringLiteral' ||
            p.key?.type === 'NumericLiteral';
        return keyOk && isStaticAstNode(p.value);
      });
    default:
      // Identifier, MemberExpression, CallExpression, BinaryExpression, etc.
      return false;
  }
}

function updateObjectExpressionStyle(
  expression: any,
  key: string,
  value: string | number,
  valueType: 'string' | 'number' | 'expression',
): void {
  const properties = expression.properties;
  const property = properties.find((it: any) => it.key?.name === key);
  const valueNode = makeStyleValueNode(value, valueType);

  if (property) {
    property.value = valueNode;
  } else {
    properties.push(objectProperty(identifier(key), valueNode));
  }
}

/**
 * Set a style property on a JSX element
 * Handles inline styles, StyleSheet references, and array styles
 *
 * @param files - The current file contents
 * @param node - The node location (file, line, col)
 * @param key - The style property name to set
 * @param value - The value to set
 * @param valueType - The type of value ('string' or 'number')
 * @returns TransformResult with updated files or error
 */
export function setStyleProperty(
  files: FileContents,
  node: NodeLocation,
  key: string,
  value: string | number,
  valueType: 'string' | 'number' | 'expression',
): TransformResult {
  const fileKey = getFileKey(node);
  const file = files[fileKey];

  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return {
      success: false,
      updatedFiles: files,
      error: `File not found or not a code file: ${fileKey}`,
    };
  }

  const fileContents = file.contents;

  try {
    const parser = createParser(fileKey);
    const ast = parse(fileContents, { parser });
    let foundNode = false;
    let stylePropName = '';
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);

    // First pass: Find the JSX element and update inline styles or get StyleSheet reference
    const matchedStylePath = findJSXElementInAST(ast, targetLine, targetCol, node?.type);
    if (matchedStylePath) {
      foundNode = true;
      const attributes = (matchedStylePath.node as any).openingElement?.attributes || [];
      const styleAttribute = attributes.find((it: any) => it.name?.name === 'style');
      // Also keep reference to the opening element for adding style prop if needed
      const _openingElement = (matchedStylePath.node as any).openingElement;

      // eslint-disable-next-line no-lone-blocks
      {
        if (styleAttribute) {
          // Unwrap `style={({ pressed }) => [...]}` callbacks to their returned
          // style node so we mutate that in place rather than appending a new attr.
          const expression = unwrapStyleExpression(styleAttribute?.value?.expression);

          if (expression?.type === 'ObjectExpression') {
            // Inline style object: style={{ flex: 1 }}
            updateObjectExpressionStyle(expression, key, value, valueType);
          } else if (expression?.type === 'MemberExpression') {
            // StyleSheet reference: style={styles.container}
            if (valueType === 'expression' && !isStaticExpression(value)) {
              // Dynamic refs (getThemeValue("colors.x"), props.size, …) can't
              // live in module-scope StyleSheet.create — wrap into an array
              // with an inline override: style={[styles.container, { key: expr }]}.
              const newInline = objectExpression([
                objectProperty(identifier(key), makeStyleValueNode(value, valueType)),
              ]);
              styleAttribute.value = jsxExpressionContainer(
                arrayExpression([expression, newInline]),
              );
            } else {
              stylePropName = expression.property.name;
            }
          } else if (expression?.type === 'ArrayExpression') {
            // Array of styles: style={[styles.container, { backgroundColor: 'red' }]}

            if (expression?.type === 'ObjectExpression') {
              // Inline style object: style={{ flex: 1 }}
              updateObjectExpressionStyle(expression, key, value, valueType);
            } else if (expression?.type === 'MemberExpression') {
              // StyleSheet reference: style={styles.container}
              if (valueType === 'expression') {
                // Dynamic refs can't live in StyleSheet.create — wrap in array with inline object
                const newInline = objectExpression([
                  objectProperty(identifier(key), makeStyleValueNode(value, valueType)),
                ]);
                styleAttribute.value = jsxExpressionContainer(
                  arrayExpression([expression, newInline]),
                );
              } else {
                stylePropName = expression.property.name;
              }
            } else if (expression?.type === 'ArrayExpression') {
              // Array of styles: style={[styles.container, { backgroundColor: 'red' }]}
              const elements = expression.elements || [];

              let inlineElement: any = null;
              let memberPropName = '';
              for (const element of elements) {
                if (element?.type === 'ObjectExpression' && !inlineElement) inlineElement = element;
                if (element?.type === 'MemberExpression' && !memberPropName)
                  memberPropName = element.property.name;
              }

              if (valueType === 'expression' && !isStaticExpression(value)) {
                // Truly dynamic refs (theme.colors.x, props.size, etc.) can't live in
                // StyleSheet.create — write to inline object only. Static literal
                // expressions ({ width: 5, height: 0 }, [{ rotate: '45deg' }]) fall
                // through to the StyleSheet path below alongside scalar values.
                if (inlineElement) {
                  updateObjectExpressionStyle(inlineElement, key, value, valueType);
                } else {
                  expression.elements.push(
                    objectExpression([
                      objectProperty(identifier(key), makeStyleValueNode(value, valueType)),
                    ]),
                  );
                }
              } else if (memberPropName) {
                // StyleSheet reference exists — always write there so all tiles update together.
                // Strip any stale inline override for this key to prevent duplication.
                stylePropName = memberPropName;
                if (inlineElement) {
                  inlineElement.properties = inlineElement.properties.filter(
                    (p: any) => !(p.key?.name === key || p.key?.value === key),
                  );
                }
              } else if (inlineElement) {
                // No StyleSheet reference — write to inline object
                updateObjectExpressionStyle(inlineElement, key, value, valueType);
              }
            }
          } else {
            // No style attribute - add inline style
            const newStyleAttr = jsxAttribute(
              jsxIdentifier('style'),
              jsxExpressionContainer(
                objectExpression([
                  objectProperty(identifier(key), makeStyleValueNode(value, valueType)),
                ]),
              ),
            );
            _openingElement.attributes.push(newStyleAttr);
          }
        } else {
          // No style attribute - add inline style
          const newStyleAttr = jsxAttribute(
            jsxIdentifier('style'),
            jsxExpressionContainer(
              objectExpression([
                objectProperty(
                  identifier(key),
                  valueType === 'string'
                    ? stringLiteral(value as string)
                    : numericLiteral(value as number),
                ),
              ]),
            ),
          );
          _openingElement.attributes.push(newStyleAttr);
        }
      }
    }

    // Second pass: If using StyleSheet, update the StyleSheet.create object
    if (stylePropName) {
      types.visit(ast, {
        visitVariableDeclaration(path) {
          if (path.node.kind === 'const' && path.node.type === 'VariableDeclaration') {
            const declaration = path.node.declarations.find((it: any) => it.id?.name === 'styles');

            if (
              declaration?.init?.type === 'CallExpression' &&
              path.node.declarations[0].init.arguments[0].type === 'ObjectExpression'
            ) {
              const targetStyleObj = path.node.declarations[0].init.arguments[0].properties.find(
                (it: any) => it.key?.name === stylePropName,
              );

              if (
                targetStyleObj?.type === 'ObjectProperty' &&
                targetStyleObj.value.type === 'ObjectExpression'
              ) {
                const properties = targetStyleObj.value.properties;
                const property = properties.find((it: any) => it.key?.name === key);

                if (property) {
                  property.value = makeStyleValueNode(value, valueType);
                } else {
                  properties.push(
                    objectProperty(identifier(key), makeStyleValueNode(value, valueType)),
                  );
                }
              }
            }
          }
          this.traverse(path);
        },
      });
    }

    if (!foundNode) {
      return {
        success: false,
        updatedFiles: files,
        error: `Node not found at line ${node.line}, col ${node.col} in ${fileKey}`,
      };
    }

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: {
        ...files,
        [fileKey]: {
          type: 'CODE' as const,
          contents: updatedCode,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Remove a style property from a JSX element's style attribute.
 * Handles inline objects, arrays with inline objects, and StyleSheet references.
 */
export function removeStyleProperty(
  files: FileContents,
  node: NodeLocation,
  key: string,
): TransformResult {
  const fileKey = getFileKey(node);
  const file = files[fileKey];

  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') {
    return {
      success: false,
      updatedFiles: files,
      error: `File not found or not a code file: ${fileKey}`,
    };
  }

  try {
    const parser = createParser(fileKey);
    const ast = parse(file.contents, { parser });
    let foundNode = false;
    let stylePropName = '';
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);

    const matchedPath = findJSXElementInAST(ast, targetLine, targetCol, (node as any).type);
    if (matchedPath) {
      foundNode = true;
      const attributes = (matchedPath.node as any).openingElement?.attributes || [];
      const styleAttr = attributes.find((it: any) => it.name?.name === 'style');

      if (styleAttr) {
        // Unwrap `style={({ pressed }) => [...]}` callbacks to their returned style node.
        const expr = unwrapStyleExpression(styleAttr?.value?.expression);

        if (expr?.type === 'ObjectExpression') {
          expr.properties = expr.properties.filter(
            (p: any) => !(p.key?.name === key || p.key?.value === key),
          );
        } else if (expr?.type === 'MemberExpression') {
          stylePropName = expr.property.name;
        } else if (expr?.type === 'ArrayExpression') {
          // Remove from inline AND StyleSheet so the property is gone entirely.
          for (const el of expr.elements || []) {
            if (el?.type === 'ObjectExpression') {
              el.properties = el.properties.filter(
                (p: any) => !(p.key?.name === key || p.key?.value === key),
              );
            }
            if (el?.type === 'MemberExpression') {
              stylePropName = el.property.name;
            }
          }
        }
      }
    }

    if (stylePropName) {
      types.visit(ast, {
        visitVariableDeclaration(path) {
          if (path.node.kind === 'const') {
            const declaration = path.node.declarations.find((it: any) => it.id?.name === 'styles');
            if (
              declaration?.init?.type === 'CallExpression' &&
              path.node.declarations[0].init.arguments[0]?.type === 'ObjectExpression'
            ) {
              const targetStyleObj = path.node.declarations[0].init.arguments[0].properties.find(
                (it: any) => it.key?.name === stylePropName,
              );
              if (
                targetStyleObj?.type === 'ObjectProperty' &&
                targetStyleObj.value.type === 'ObjectExpression'
              ) {
                targetStyleObj.value.properties = targetStyleObj.value.properties.filter(
                  (it: any) => !(it.key?.name === key || it.key?.value === key),
                );
              }
            }
          }
          this.traverse(path);
        },
      });
    }

    if (!foundNode) {
      return {
        success: false,
        updatedFiles: files,
        error: `Node not found at line ${node.line}, col ${node.col} in ${fileKey}`,
      };
    }

    const updatedCode = print(ast).code;
    return {
      success: true,
      updatedFiles: {
        ...files,
        [fileKey]: { type: 'CODE' as const, contents: updatedCode },
      },
    };
  } catch (error) {
    return {
      success: false,
      updatedFiles: files,
      error: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
